import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { ERROR_MESSAGES } from "./errorMessages";

/**
 * Error context for Sentry logging
 */
export interface ErrorContext {
  context: string; // e.g., "handleReceiveItems", "createPayment"
  component?: string; // e.g., "PurchaseOrderDetailPage"
  metadata?: Record<string, any>; // Any additional context
}

/**
 * Extended error options with user message and toast control
 */
export interface ErrorOptions extends ErrorContext {
  userMessage?: string; // Custom user-facing message (optional)
  showToast?: boolean; // Whether to show toast (default: true)
  level?: "error" | "warning" | "info"; // Sentry level (default: 'error')
}

/**
 * Parsed error information
 */
export interface ParsedError {
  code: string | null; // Error code (PGRST203, 23505, etc.)
  message: string; // Technical error message
  hint?: string; // PostgreSQL hint if available
  details?: string; // Additional details
  userMessage: string; // User-friendly message
}

/**
 * Type guard for Supabase PostgrestError
 */
function isPostgrestError(error: any): error is {
  code: string;
  message: string;
  details?: string;
  hint?: string;
} {
  return (
    error &&
    typeof error === "object" &&
    "code" in error &&
    typeof error.code === "string" &&
    "message" in error
  );
}

/**
 * Type guard for PostgreSQL constraint errors
 */
function isPostgresConstraintError(error: any): boolean {
  if (!isPostgrestError(error)) return false;

  // PostgreSQL error codes
  const pgErrorCodes = [
    "23505", // unique_violation
    "23503", // foreign_key_violation
    "23502", // not_null_violation
    "23514", // check_violation
    "23P01", // exclusion_violation
  ];

  return pgErrorCodes.includes(error.code);
}

/**
 * Type guard for network errors
 */
function isNetworkError(error: any): boolean {
  if (error instanceof TypeError && error.message.includes("fetch")) {
    return true;
  }

  if (error && typeof error === "object") {
    const message = error.message?.toLowerCase() || "";
    return (
      message.includes("network") ||
      message.includes("timeout") ||
      message.includes("connection") ||
      message.includes("cors")
    );
  }

  return false;
}

/**
 * Type guard for authentication errors
 */
function isAuthError(error: any): boolean {
  if (!isPostgrestError(error)) return false;

  const authCodes = ["PGRST301", "PGRST302"];
  return authCodes.includes(error.code);
}

/**
 * Parse Supabase error and extract relevant information
 */
export function parseSupabaseError(error: any): ParsedError {
  // Handle Supabase Postgrest errors
  if (isPostgrestError(error)) {
    const userMessage =
      ERROR_MESSAGES[error.code] || ERROR_MESSAGES.UNKNOWN;

    return {
      code: error.code,
      message: error.message,
      hint: error.hint,
      details: error.details,
      userMessage,
    };
  }

  // Handle network errors
  if (isNetworkError(error)) {
    return {
      code: "NETWORK_ERROR",
      message: error.message || "Network error",
      userMessage: ERROR_MESSAGES.NETWORK_ERROR,
    };
  }

  // Handle generic errors
  const message =
    error instanceof Error ? error.message : String(error);

  return {
    code: null,
    message,
    userMessage: ERROR_MESSAGES.UNKNOWN,
  };
}

/**
 * Get user-friendly error message without side effects
 */
export function getErrorMessage(error: unknown): string {
  const parsed = parseSupabaseError(error);
  return parsed.userMessage;
}

/**
 * Log error to Sentry with context (no toast)
 */
export function handleError(
  error: unknown,
  context: ErrorContext
): void {
  const parsed = parseSupabaseError(error);

  // Add breadcrumb
  Sentry.addBreadcrumb({
    category: "error",
    message: context.context,
    level: "error",
    data: {
      component: context.component,
      ...context.metadata,
    },
  });

  // Set tags
  Sentry.setTags({
    error_code: parsed.code || "unknown",
    component: context.component || "unknown",
    context: context.context,
  });

  // Capture exception with extra context
  Sentry.captureException(error, {
    level: "error",
    extra: {
      parsedError: parsed,
      context: context.context,
      component: context.component,
      metadata: context.metadata,
    },
  });

  // Log to console in development
  if (import.meta.env.DEV) {
    console.error("[Error Handler]", {
      context: context.context,
      component: context.component,
      error,
      parsed,
      metadata: context.metadata,
    });
  }
}

/**
 * Show toast notification and log to Sentry (most common usage)
 */
export function handleAndNotify(
  error: unknown,
  options: ErrorOptions
): void {
  const {
    userMessage: customMessage,
    showToast = true,
    level = "error",
    ...context
  } = options;

  const parsed = parseSupabaseError(error);
  const displayMessage = customMessage || parsed.userMessage;

  // Add breadcrumb
  Sentry.addBreadcrumb({
    category: "error",
    message: context.context,
    level: level,
    data: {
      component: context.component,
      userMessage: displayMessage,
      ...context.metadata,
    },
  });

  // Set tags
  Sentry.setTags({
    error_code: parsed.code || "unknown",
    component: context.component || "unknown",
    context: context.context,
  });

  // Capture exception with extra context
  Sentry.captureException(error, {
    level: level,
    extra: {
      parsedError: parsed,
      context: context.context,
      component: context.component,
      metadata: context.metadata,
      userMessage: displayMessage,
    },
  });

  // Show toast to user
  if (showToast) {
    toast.error(displayMessage);
  }

  // Log to console in development
  if (import.meta.env.DEV) {
    console.error("[Error Handler]", {
      context: context.context,
      component: context.component,
      error,
      parsed,
      metadata: context.metadata,
      userMessage: displayMessage,
    });
  }

  // Mark error as handled (for TanStack Query global handler)
  if (error && typeof error === "object") {
    (error as any).__handled = true;
  }
}
